// DiagramViewer.js
//
// Build 28 (item 1): read-only renderer for diagrams hosted in a
// FloatingWindow on the desktop. Editing still happens in the full
// Diagramming app — this is the snapshot/compare view, mirroring the
// read-only philosophy of EntityViewer.
//
// DRY: reuses Excalidraw's native viewModeEnabled for whiteboards and
// the EXACT custom nodeTypes/DiamondNode exported from FlowchartEditor
// for flowcharts — no duplicated node rendering.

import React, { useState, useEffect } from 'react';
import { Excalidraw } from '@excalidraw/excalidraw';
import ReactFlow, { Background, Controls, MarkerType } from 'reactflow';
import { nodeTypes } from './FlowchartEditor';
import { urlToDataUrl } from '../services/imageUploadService';
import 'reactflow/dist/style.css';

function DiagramViewer({ diagram }) {
  // Hooks run unconditionally (Rules of Hooks) — before any early
  // return. wbApi is only consumed by the whiteboard branch.
  const [wbApi, setWbApi] = useState(null);

  useEffect(() => {
    if (!wbApi || !diagram) return;
    let cancelled = false;
    (async () => {
      try {
        // Build 28 (item 2): images are stored in Storage as a fileMap
        // of { fileId: { url, mimeType } }. Fetch each back to a dataURL
        // and feed Excalidraw so they render in the read-only view too.
        const fileMap = diagram.content?.fileMapJSON
          ? JSON.parse(diagram.content.fileMapJSON)
          : null;
        if (!fileMap || Object.keys(fileMap).length === 0) return;
        const files = [];
        for (const [fileId, meta] of Object.entries(fileMap)) {
          if (!meta || !meta.url) continue;
          const dataURL = await urlToDataUrl(meta.url);
          files.push({
            id: fileId,
            dataURL,
            mimeType: meta.mimeType || 'image/png',
            created: Date.now()
          });
        }
        if (!cancelled && files.length > 0 && wbApi.addFiles) {
          wbApi.addFiles(files);
        }
      } catch (e) {
        console.error('Failed to restore whiteboard images (viewer):', e);
      }
    })();
    return () => { cancelled = true; };
  }, [wbApi, diagram]);

  if (!diagram) return null;

  if (diagram.mode === 'flowchart') {
    const nodes = diagram.content?.nodes || [];
    const edges = (diagram.content?.edges || []).map(edge => ({
      ...edge,
      markerEnd: edge.markerEnd || { type: MarkerType.ArrowClosed }
    }));

    return (
      <div style={{ position: 'absolute', inset: 0, background: '#fff' }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          fitView
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          panOnDrag
          zoomOnScroll
          proOptions={{ hideAttribution: true }}
        >
          <Background />
          <Controls showInteractive={false} />
        </ReactFlow>
      </div>
    );
  }

  // Whiteboard — Excalidraw in native read-only (viewModeEnabled).
  const initialData = {
    elements: diagram.content?.elementsJSON
      ? JSON.parse(diagram.content.elementsJSON)
      : (diagram.content?.elements || []),
    appState: diagram.content?.appStateJSON
      ? JSON.parse(diagram.content.appStateJSON)
      : (diagram.content?.appState || {}),
    scrollToContent: true
  };

  return (
    <div style={{ position: 'absolute', inset: 0, background: '#fff' }}>
      <Excalidraw
        excalidrawAPI={(api) => setWbApi(api)}
        initialData={initialData}
        viewModeEnabled
        UIOptions={{ canvasActions: { export: false, saveToActiveFile: false } }}
      />
    </div>
  );
}

export default DiagramViewer;
